import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { 
  ShoppingCartOutlined,
  ArrowLeftOutlined,
  HeartOutlined,
  SafetyOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import calmiImage from '../../../assets/images/Calmi.png';

const { Title, Text, Paragraph } = Typography;

const CalmiClassic = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('lumi-classic', 'Lumi Classic', 499);
  }, []);

  const product = {
    id: 'lumi-classic',
    name: 'Lumi Classic',
    price: 499,
    image: calmiImage,
    collection: 'Core Collection',
    subtitle: 'The original cozy companion',
    description: 'Our flagship teddy bear in warm honey brown with the softest plush exterior. Perfect for daily comfort and emotional support. Features our signature weighted design that feels like a gentle, continuous hug. The classic bear design appeals to all ages and personalities.',
    fullDescription: 'Lumi Classic is where it all began. This warm honey brown teddy bear represents the perfect balance of comfort, technology, and emotional intelligence. Designed for children who need consistent emotional support throughout their daily routines, Lumi Classic provides the foundational experience that makes every interaction meaningful. The classic teddy bear design ensures universal appeal while the sophisticated AI ensures age-appropriate conversations that grow with your child.',
    specs: { 
      dimensions: '12" x 8" x 6"', 
      color: 'Honey Brown', 
      texture: 'Classic Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Signature weighted therapy core for gentle pressure comfort',
      'Warm honey brown plush in ultra-soft, huggable material',
      'AI conversations that adapt to your child\'s emotional needs',
      'Perfect starter companion for families new to Lumi',
      'Timeless teddy bear design loved by all ages',
      'Ideal for daily routines, bedtime, and stressful moments'
    ],
    idealFor: [
      'Children new to weighted therapy',
      'Daily emotional support and comfort',
      'Building healthy emotional vocabulary',
      'Consistent bedtime routines',
      'Managing everyday stress and transitions',
      'Families wanting the classic Lumi experience'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    console.log('Payment successful:', paymentIntent);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Family
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🧸</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '10px'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="purple">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                {/* Preorder Info */}
                <Card size="small" style={{ backgroundColor: '#f0f8ff' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong>Weight:</Text><br/>
              <Text>{product.specs.weight}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Dimensions:</Text><br/>
              <Text>{product.specs.dimensions}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Color:</Text><br/>
              <Text>{product.specs.color}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Texture:</Text><br/>
              <Text>{product.specs.texture}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Battery Life:</Text><br/>
              <Text>{product.specs.batteryLife}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Charging:</Text><br/>
              <Text>{product.specs.charging}</Text>
            </Col>
          </Row>
        </Card>

        {/* Features */}
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        {/* Benefits */}
        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose Lumi Classic?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#e74c3c', marginBottom: '16px' }} />
              <Title level={4}>Perfect First Companion</Title>
              <Text>
                The ideal introduction to Lumi's world of emotional intelligence and comfort therapy.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={4}>Proven Comfort Design</Title>
              <Text>
                Classic teddy bear styling that children instinctively love and trust.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
              <Title level={4}>Balanced Experience</Title>
              <Text>
                Perfect weight and size for daily use, neither too light nor too heavy.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Checkout Modal */}
      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiClassic;